import { DateTime } from "@bps/date-time";
import {
  DefaultButton,
  Heading,
  Stack,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { RolloutFormValues } from "./rolloutFormValues";

const TIME_FORMAT = "HH:mm";

interface RolloutDateShortcutDto {
  label: string;
  startDate: DateTime;
  windowDuration: Duration;
  intervalDuration: Duration;
}

interface RolloutDateShortcutsProps {
  isReadOnly: boolean;
}

export const RolloutDateShortcuts: React.FC<RolloutDateShortcutsProps> = ({
  isReadOnly
}) => {
  const theme = useTheme();

  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const dateShortcuts: RolloutDateShortcutDto[] = [
    {
      label: "Immediately",
      startDate: DateTime.now()
        .minus({ hours: 4 })
        .startOf("hour"),
      windowDuration: { minutes: 5 },
      intervalDuration: { minutes: 5 }
    },
    {
      label: "End of day",
      startDate: DateTime.now()
        .startOf("day")
        .plus({ hours: 18 }),
      windowDuration: { minutes: 30 },
      intervalDuration: { minutes: 30 }
    },
    {
      label: "Midnight",
      startDate: DateTime.now()
        .endOf("day")
        .plus({ minute: 1 }),
      windowDuration: { minutes: 30 },
      intervalDuration: { minutes: 15 }
    },
    {
      label: "Weekend",
      startDate: DateTime.now()
        .endOf("week")
        .minus({ day: 1 })
        .plus({ minute: 1 }),
      windowDuration: { minutes: 60 },
      intervalDuration: { minutes: 30 }
    }
  ];

  const { actions, state } = useFormContext<RolloutFormValues>();

  const setDateRanges = (shortcut: RolloutDateShortcutDto) => {
    const { startDate, windowDuration, intervalDuration } = shortcut;

    const utcStart = startDate.setZone("utc");

    const initialDownloadDateStartUtc = utcStart.toJSDate();

    const initialDownloadDateEndUtc = utcStart.plus(windowDuration).toJSDate();

    const initialDownloadTimeStartUtc = utcStart.toFormat(TIME_FORMAT);
    const initialDownloadTimeEndUtc = utcStart
      .plus(windowDuration)
      .toFormat(TIME_FORMAT);

    const initialInstallDateStartUtc = utcStart
      .plus(windowDuration)
      .plus(intervalDuration)
      .toJSDate();

    const initialInstallDateEndUtc = utcStart
      .plus(windowDuration)
      .plus(intervalDuration)
      .plus(windowDuration)
      .toJSDate();

    const initialInstallTimeStartUtc = utcStart
      .plus(windowDuration)
      .plus(intervalDuration)
      .toFormat(TIME_FORMAT);

    const initialInstallTimeEndUtc = utcStart
      .plus(windowDuration)
      .plus(intervalDuration)
      .plus(windowDuration)
      .toFormat(TIME_FORMAT);

    const newValues = {
      ...state.values,
      initialDownloadDateStartUtc,
      initialDownloadDateEndUtc,
      initialDownloadTimeStartUtc,
      initialDownloadTimeEndUtc,
      initialInstallDateStartUtc,
      initialInstallDateEndUtc,
      initialInstallTimeStartUtc,
      initialInstallTimeEndUtc
    };

    actions.setValues(newValues);
  };

  return (
    <Stack>
      <Heading labelPaddings>Date Presets</Heading>
      <Stack horizontal tokens={{ childrenGap: theme.spacing.s1 }}>
        {dateShortcuts.map(dateShortcut => {
          return (
            <DefaultButton
              key={dateShortcut.label}
              onClick={() => setDateRanges(dateShortcut)}
              disabled={isReadOnly || !hasDeployPermission}
              title={!hasDeployPermission ? NO_ACTION_PERMISSION : undefined}
            >
              {dateShortcut.label}
            </DefaultButton>
          );
        })}
      </Stack>
    </Stack>
  );
};
